import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import logoutIcon from "../../assets/icons/logout.svg";
import email from "../../assets/images/email.png";
import { api } from "../../services/axiosService";
import Toaster from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

export default function EmailVerify() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { handleLogout, user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      const userData = await refreshUser();
      if (userData?.emailVerified) {
        navigate("/onboarding/welcome");
      } else {
        toast({
          description: "Email verification required. Please check your inbox.",
          variant: "error",
        });
      }
    } catch (error) {
      console.log(error, "err");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      const response = await api.get("/api/v1/user/verify-email");
      console.log("Resend email response:", response.data);
      if (response?.data?.data?.status === "pending") {
        toast({
          description: "Resend email successfully.",
          variant: "success",
        });
      }
    } catch (error) {
      toast({
        description: error,
        variant: "error",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex flex-col overflow-y-auto w-full ">
      <div className="flex  border-0 justify-between items-center pt-[3.75rem] pb-[1.75rem] lg:pt-[1.75rem] lg:px-16 px-5 lg:pb-[1.5rem] lg:border lg:border-b lg:border-b-[#EBE9EF]">
        <div className="w-24">
          <button
            onClick={handleBack}
            className=" w-12 h-12 flex items-center justify-center rounded-full border border-[#EBE9EF]"
          >
            <ArrowLeft className="w-5 h-5 text-[#6D6584]" />
          </button>
        </div>
        <p className="font-plusjakarta font-[600] font-semibold text-[16px] lg:text-[20px] leading-[24px] tracking-normal text-center text-[#33125F]">
          Verify email
        </p>
        <div className="w-24 flex justify-end items-center">
          <button
            onClick={handleLogout}
            className="w-12 h-12 flex items-center lg:hidden block justify-center rounded-full border border-[#EBE9EF]"
          >
            <img src={logoutIcon} alt="" className="w-[16px] h-[16px]" />
          </button>

          <button
            onClick={handleLogout}
            className="rounded-full border lg:block hidden border-[#EBE9EF] flex items-center justify-center px-5 py-[13px] text-[14px] font-medium text-[#6D6584]"
          >
            Log out
          </button>
        </div>
      </div>
      <div className=" h-[4px] bg-gray-200 rounded-full w-full lg:hidden block ">
        {" "}
      </div>
      <div className="z-10 mt-[1.5rem] flex flex-col items-center justify-center min-h-[70vh]">
        <div className="lg:mt-[50px] mt-[40px]">
          <img
            src={email}
            alt="Email Verification"
            className="mx-auto w-[194px] h-[136px]"
          />
        </div>
        <p className="font-plusjakarta font-semibold text-[32px] leading-[40px] tracking-normal text-center text-[#33125F] mt-7 mb-7">
          You've got mail!
        </p>
        <div className="lg:max-w-[393px] max-w-[326px] mb-5">
          <p className="font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal text-center mb-7 text-[#423E51]">
            We've sent a verification link to{" "}
            <span className="font-semibold">{user?.email}.</span> Check your
            email and click the link to continue.
          </p>
          <p className="font-plusjakarta font-normal italic text-[16px] leading-[24px] tracking-normal text-center mb-7 text-[#423E51]">
            Can't find it?
            <br />
            Be sure to check your junk or spam folder - sometimes it hides
            there!
          </p>
        </div>
        <div className="w-[100%] gap-4 flex flex-col justify-center items-center lg:mt-0 my-5">
          <button
            className={`
              ${
                !isLoading
                  ? "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white"
                  : "bg-[#F2F1F4] cursor-not-allowed text-[#9D96AF]"
              }
                flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded !w-[326px] lg:max-w-[358px] ${
                  isLoading ? "cursor-not-allowed opacity-70" : ""
                } rounded-full transition duration-300 ease-in-out hover:scale-[1.1] `}
            disabled={isLoading}
            onClick={handleContinue}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#9D96AF]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              "Continue"
            )}
          </button>
          <button
            className={`${
              isResending
                ? "bg-[#F2F1F4] cursor-not-allowed text-[#9D96AF]"
                : "bg-transparent border border-[#EBE9EF] text-[#6D6584]"
            } 
              flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded !w-[326px] lg:max-w-[358px] rounded-full transition duration-300 ease-in-out hover:scale-[1.1] ${
                isResending ? "cursor-not-allowed opacity-70" : ""
              }`}
            disabled={isResending}
            onClick={handleResendEmail}
          >
            {isResending ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#9D96AF]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending...
              </>
            ) : (
              "Resend email"
            )}
          </button>
        </div>
      </div>
      <div className="mt-auto  border border-[#EBE9EF] flex justify-center items-center left-0 w-full lg:px-[23px] px-[27px] lg:pt-[23px] pt-[15px] lg:pb-[23px] pb-[35px]">
        <p className="font-plusjakarta font-normal text-[16px] leading-[24px] tracking-normal text-[#423E51]">
          Already have an account?{" "}
          <p
            onClick={handleLogout}
            className="text-[#33125F] font-semibold ml-2 inline cursor-pointer"
          >
            Sign in
          </p>
        </p>
      </div>
      <Toaster />
    </div>
  );
}
